import React from "react";
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  Tooltip,
  Typography,
} from "@mui/material";
import { makeStyles } from "@mui/styles";
import VisibilityIcon from "@mui/icons-material/Visibility";
import ReplayIcon from "@mui/icons-material/Replay";
import DeleteIcon from "@mui/icons-material/Delete";
import {
  FailedJob,
  DelayedJob,
  QueueName,
  JOB_NAME_LABELS,
  QUEUE_LABELS,
  JOB_STATE_COLORS,
} from "types/queue";
import sessionHelper from "services/sessionHelper";
import { PermittedRoles } from "utils/permissions";

const useStyles = makeStyles((theme: any) => ({
  tableContainer: {
    boxShadow: "0 2px 8px rgba(0,0,0,0.1) !important",
    borderRadius: "8px !important",
    overflow: "hidden",
  },
  tableHead: {
    backgroundColor: "#1d1030",
    "& th": {
      color: "#fff",
      fontWeight: 600,
      fontSize: 12,
      padding: "12px 16px",
    },
  },
  tableRow: {
    "&:nth-of-type(odd)": {
      backgroundColor: "#fafafa",
    },
    "&:hover": {
      backgroundColor: "#f0f0f0",
    },
  },
  tableCell: {
    padding: "10px 16px",
    fontSize: 13,
  },
  queueChip: {
    fontWeight: 600,
    fontSize: 11,
  },
  stateChip: {
    fontWeight: 600,
    fontSize: 11,
  },
  failedReason: {
    maxWidth: 300,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    color: "#c62828",
    fontSize: 12,
  },
  actions: {
    display: "flex",
    gap: 4,
  },
  noData: {
    textAlign: "center",
    padding: "40px 20px",
    color: "#666",
  },
}));

interface JobsTableProps {
  jobs: (FailedJob | DelayedJob)[];
  queueName: QueueName;
  onViewDetails: (job: FailedJob | DelayedJob, queueName?: QueueName) => void;
  onRetry?: (job: FailedJob | DelayedJob, queueName?: QueueName) => void;
  onRemove?: (job: FailedJob | DelayedJob, queueName?: QueueName) => void;
  showRetry?: boolean;
  showRemove?: boolean;
}

const JobsTable: React.FC<JobsTableProps> = ({
  jobs,
  queueName,
  onViewDetails,
  onRetry,
  onRemove,
  showRetry = true,
  showRemove = true,
}) => {
  const classes = useStyles();

  const hasRetryPermission = sessionHelper.hasRole(
    PermittedRoles.QUEUE_MONITORING.RETRY_ROLES
  );
  const hasAdminPermission = sessionHelper.hasRole(
    PermittedRoles.QUEUE_MONITORING.ADMIN_ROLES
  );

  const formatTimestamp = (ts: number) => {
    return new Date(ts).toLocaleString();
  };

  const getStateColor = (state: string) => {
    return (
      JOB_STATE_COLORS[state as keyof typeof JOB_STATE_COLORS] || "#9e9e9e"
    );
  };

  // Check if any job has failedReason to show Error column consistently
  const hasErrorColumn = jobs.some(
    (job) => "failedReason" in job && (job as FailedJob).failedReason
  );

  if (jobs.length === 0) {
    return (
      <Box className={classes.noData}>
        <Typography variant="body1">No jobs found</Typography>
      </Box>
    );
  }

  return (
    <TableContainer component={Paper} className={classes.tableContainer}>
      <Table size="small">
        <TableHead className={classes.tableHead}>
          <TableRow>
            <TableCell>Job ID</TableCell>
            <TableCell>Name</TableCell>
            <TableCell>State</TableCell>
            <TableCell>Attempts</TableCell>
            <TableCell>Created</TableCell>
            {hasErrorColumn && <TableCell>Error</TableCell>}
            <TableCell align="right">Actions</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {jobs.map((job) => (
            <TableRow
              key={`${queueName}-${job.id}`}
              className={classes.tableRow}
            >
              <TableCell className={classes.tableCell}>
                <Typography variant="body2" fontWeight={500}>
                  {job.id}
                </Typography>
              </TableCell>
              <TableCell className={classes.tableCell}>
                {JOB_NAME_LABELS[job.name] || job.name}
              </TableCell>
              <TableCell className={classes.tableCell}>
                <Chip
                  label={job.state.toUpperCase()}
                  size="small"
                  className={classes.stateChip}
                  sx={{
                    backgroundColor: getStateColor(job.state),
                    color: "#fff",
                  }}
                />
              </TableCell>
              <TableCell className={classes.tableCell}>
                {job.attemptsMade} / {job.opts?.attempts || "∞"}
              </TableCell>
              <TableCell className={classes.tableCell}>
                {formatTimestamp(job.timestamp)}
              </TableCell>
              {hasErrorColumn && (
                <TableCell className={classes.tableCell}>
                  {"failedReason" in job && (job as FailedJob).failedReason ? (
                    <Tooltip title={(job as FailedJob).failedReason || ""}>
                      <Typography className={classes.failedReason}>
                        {(job as FailedJob).failedReason}
                      </Typography>
                    </Tooltip>
                  ) : (
                    <Typography className={classes.tableCell}>-</Typography>
                  )}
                </TableCell>
              )}
              <TableCell className={classes.tableCell} align="right">
                <Box className={classes.actions}>
                  <Tooltip title="View Details">
                    <IconButton
                      size="small"
                      onClick={() => onViewDetails(job, queueName)}
                      color="primary"
                    >
                      <VisibilityIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  {showRetry &&
                    hasRetryPermission &&
                    job.state === "failed" && (
                      <Tooltip title="Retry Job">
                        <IconButton
                          size="small"
                          onClick={() => onRetry?.(job, queueName)}
                          color="success"
                        >
                          <ReplayIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                  {showRemove && hasAdminPermission && (
                    <Tooltip title="Remove Job">
                      <IconButton
                        size="small"
                        onClick={() => onRemove?.(job, queueName)}
                        color="error"
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  )}
                </Box>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export default JobsTable;
